import React, { useState } from 'react';
import { useDarkMode } from '../../context/DarkModeContext';
import { useUserRole } from '../../context/UserRoleContext';
import { UpdatesFeed } from '../../components/Trove/UpdatesFeed';
import { UpdatePost, UpdatePostType } from '../../components/Trove/UpdatesPostCard';
import { TroveLayout } from '../../components/Trove/TroveLayout';
import { FilterSection } from '../../components/UI/FilterSidebar';
// Mock Data
const mockUpdates: UpdatePost[] = [{
  id: 1,
  type: 'New Release',
  title: 'Iron Flame',
  description: 'Rebecca Yarros continues the Empyrean series in this stunning sequel.',
  author: 'Rebecca Yarros',
  date: 'Oct 15, 2023',
  image: 'https://images.unsplash.com/photo-1518709268805-4e9042af9f23?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  genre: 'Fantasy',
  seriesName: 'The Empyrean',
  bookNumber: 2,
  rating: 4.7,
  reviewCount: 18420,
  formats: ['Hardcover', 'Ebook', 'Audiobook'],
  isNew: true
}, {
  id: 2,
  type: 'Cover Reveal',
  title: 'The Familiar — Leigh Bardugo',
  description: "First look at the cover for Leigh Bardugo's upcoming standalone fantasy novel.",
  author: 'Leigh Bardugo',
  date: 'Oct 14, 2023',
  genre: 'Fantasy',
  isNew: true
}, {
  id: 3,
  type: 'Audiobook Launch',
  title: 'Happy Place — Full Cast Audio',
  description: "Emily Henry's beloved romance now available as a full cast audiobook.",
  author: 'Emily Henry',
  date: 'Oct 13, 2023',
  genre: 'Romance',
  narrator: 'Julia Whelan & Zachary Webber',
  rating: 4.5,
  reviewCount: 3210,
  formats: ['Audiobook'],
  availability: 'Audible, Libro.fm',
  isNew: true
}, {
  id: 4,
  type: 'Vendor Availability',
  title: 'Romance Cover Designer — Open for Q1 2024',
  description: 'Specialising in contemporary and paranormal romance. 5 slots available. DM for rates.',
  author: 'Luna Design Co.',
  date: 'Oct 15, 2023',
  serviceCategory: 'Cover Design & Branding',
  slots: 5,
  turnaround: '3–4 week',
  priceRange: '$350–$800',
  availability: 'Remote',
  isNew: true
}, {
  id: 5,
  type: 'Collaboration Request',
  title: 'Co-Author Wanted — Fantasy Series',
  description: 'Established fantasy author seeking co-writer for a new 5-book epic fantasy series.',
  author: 'D. Morgan',
  date: 'Oct 14, 2023',
  genre: 'Fantasy',
  slots: 1,
  deadline: 'Nov 15, 2023'
}, {
  id: 6,
  type: 'Service Launch',
  title: 'New Formatting Packages for Indie Authors',
  description: 'Professional interior formatting for print and ebook. Launch pricing available until Nov 1st.',
  author: 'FormatPro Services',
  date: 'Oct 12, 2023',
  serviceCategory: 'Formatting & Production',
  priceRange: '$150–$400',
  turnaround: '5–7 day',
  availability: 'Remote'
}, {
  id: 7,
  type: 'Award Nomination',
  title: 'Indie Lit Awards — Submission Deadline Nov 30',
  description: 'Submit your work for consideration across 12 categories. All indie authors eligible.',
  author: 'Indie Lit Awards',
  date: 'Oct 11, 2023',
  deadline: 'Nov 30, 2023'
}, {
  id: 8,
  type: 'Sale',
  title: 'Spooky Season Sale — 50% Off Horror Titles',
  description: 'Get half off selected horror and thriller titles through October 31st.',
  author: 'Various Authors',
  date: 'Oct 10, 2023',
  genre: 'Horror',
  originalPrice: 9.99,
  salePrice: 4.99,
  saleEndsAt: '2023-10-31'
}, {
  id: 9,
  type: 'Event Announcement',
  title: 'Virtual Book Signing with Sarah J. Maas',
  description: 'Join us for an exclusive virtual event discussing the latest Crescent City release.',
  author: 'Sarah J. Maas',
  date: 'Oct 09, 2023',
  genre: 'Fantasy',
  eventDate: 'Nov 3, 2023 · 6:00 PM EST',
  availability: 'Virtual — Zoom'
}];
const UpdatesPage = () => {
  const {
    darkMode
  } = useDarkMode();
  const {
    isReader
  } = useUserRole();
  // Lifted state for filters
  const [activeTypeFilter, setActiveTypeFilter] = useState<UpdatePostType | 'All' | 'Industry'>('All');
  const [activeServiceFilter, setActiveServiceFilter] = useState<string | 'All'>('All');
  const [activeGenreFilter, setActiveGenreFilter] = useState<string | 'All'>('All');
  // Define filter sections for sidebar
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Update Type',
    type: 'radio',
    value: activeTypeFilter,
    onChange: val => setActiveTypeFilter(val),
    options: [{
      value: 'All',
      label: 'All Updates'
    }, ...(!isReader ? [{
      value: 'Industry',
      label: 'Industry Only'
    }] : []), {
      value: 'New Release',
      label: 'New Releases'
    }, {
      value: 'Cover Reveal',
      label: 'Cover Reveals'
    }, {
      value: 'Audiobook Launch',
      label: 'Audiobook Launches'
    }, {
      value: 'Sale',
      label: 'Sales'
    }, {
      value: 'Event Announcement',
      label: 'Events'
    }, {
      value: 'Award Nomination',
      label: 'Awards'
    }, ...(!isReader ? [{
      value: 'Vendor Availability',
      label: 'Vendor Availability'
    }, {
      value: 'Collaboration Request',
      label: 'Collaboration Requests'
    }, {
      value: 'Service Launch',
      label: 'Service Launches'
    }] : [])]
  }, {
    id: 'genre',
    title: 'Genre',
    type: 'radio',
    value: activeGenreFilter,
    onChange: val => setActiveGenreFilter(val),
    options: [{
      value: 'All',
      label: 'All Genres'
    }, {
      value: 'Romance',
      label: 'Romance'
    }, {
      value: 'Fantasy',
      label: 'Fantasy'
    }, {
      value: 'Science Fiction',
      label: 'Science Fiction'
    }, {
      value: 'Mystery',
      label: 'Mystery'
    }, {
      value: 'Thriller',
      label: 'Thriller'
    }, {
      value: 'Horror',
      label: 'Horror'
    }]
  }, ...(!isReader ? [{
    id: 'service',
    title: 'Service Category',
    type: 'radio' as const,
    value: activeServiceFilter,
    onChange: (val: any) => setActiveServiceFilter(val),
    options: [{
      value: 'All',
      label: 'All Services'
    }, {
      value: 'Editing & Manuscript Development',
      label: 'Editing & Development'
    }, {
      value: 'Cover Design & Branding',
      label: 'Cover Design & Branding'
    }, {
      value: 'Audio & Narration',
      label: 'Audio & Narration'
    }, {
      value: 'Marketing & Promotion',
      label: 'Marketing & Promotion'
    }, {
      value: 'Publishing & Distribution',
      label: 'Publishing & Distribution'
    }]
  }] : [])];
  const handleReset = () => {
    setActiveTypeFilter('All');
    setActiveServiceFilter('All');
    setActiveGenreFilter('All');
  };
  return <TroveLayout filterSections={filterSections} onResetFilters={handleReset} data-id="element-17646" data-mp="13raivjj7nl93">
      {/* Header */}
      <div className="mb-8" data-id="element-17647" data-mp="yshd6da60yjh">
        <h1 className={`text-3xl sm:text-4xl font-bold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-17648" data-mp="yt8xut285buz">
          Trove Updates
        </h1>
        <p className={`text-base max-w-2xl ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-17649" data-mp="hw1ufl2obzlx">
          {isReader ? 'Stay up to date with new releases, cover reveals, sales, and events from your favorite authors and genres.' : 'Track industry activity, find collaboration opportunities, and discover new vendor services.'}
        </p>
      </div>

      {/* Feed */}
      <UpdatesFeed posts={mockUpdates} title={isReader ? 'Latest Updates' : 'Industry Updates'} showFilters={false} maxPosts={20} activeTypeFilter={activeTypeFilter} setActiveTypeFilter={setActiveTypeFilter} activeServiceFilter={activeServiceFilter} setActiveServiceFilter={setActiveServiceFilter} activeGenreFilter={activeGenreFilter} setActiveGenreFilter={setActiveGenreFilter} data-id="element-17650" data-mp="3v4emysg1ny5o" />
    </TroveLayout>;
};
export default UpdatesPage;